import { useTranslate } from '@refinedev/core';
import { Avatar, Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import type { OAuthTokenOut, UserOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { OAUTH_TOKEN_SHOW_TABS, unixToIso } from '../constants';
import type { OAuthTokenShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<OAuthTokenShowTab, string> = {
  general: 'iam.oauth_tokens.tabs.general',
};

interface OAuthTokenShowHeaderProps {
  record: OAuthTokenOut;
  user?: UserOut;
  activeTab: OAuthTokenShowTab;
  onTabChange: (key: string) => void;
}

export const OAuthTokenShowHeader = ({
  record,
  user,
  activeTab,
  onTabChange,
}: OAuthTokenShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const revoked = record.revoked ?? false;

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <Avatar
          size={64}
          icon={<SafetyOutlined />}
          style={{
            backgroundColor: revoked ? token.colorError : token.colorPrimary,
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.device_name || record.client_id}
            </Title>
            <Tag color={revoked ? 'error' : 'success'}>
              {t(`iam.oauth_tokens.status.${revoked ? 'revoked' : 'active'}`)}
            </Tag>
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('iam.oauth_tokens.fields.user')}>
              <ShowIconLink resource="iam.users" id={record.user_id}>
                {user?.email ?? user?.username ?? record.user_id}
              </ShowIconLink>
            </StatCell>
            <StatCell label={t('iam.oauth_tokens.fields.client_id')}>
              {orDash(record.client_id)}
            </StatCell>
            <StatCell label={t('iam.oauth_tokens.fields.issued_at')}>
              {orDash(formatDateTime(unixToIso(record.issued_at)))}
            </StatCell>
            <StatCell label={t('iam.oauth_tokens.fields.expires_in')}>
              {`${record.expires_in} ${t('common.seconds')}`}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={OAUTH_TOKEN_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
